import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, 
  Mail, 
  Phone,
  MessageSquare,
  RefreshCw,
  Filter
} from 'lucide-react';
import { getAdminContacts, updateContactStatus } from '../api';
import { toast } from '../hooks/use-toast';

const AdminContacts = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);

  const statusColors = {
    new: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    resolved: 'bg-green-100 text-green-800'
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (!response.ok) navigate('/admin/login');
      } catch {
        navigate('/admin/login');
      }
    };
    checkAuth();
  }, [navigate]);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const data = await getAdminContacts(params);
      setContacts(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load contacts', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
  }, [statusFilter]);

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await updateContactStatus(ticketId, newStatus);
      toast({ title: 'Updated', description: `Status changed to ${newStatus.replace('_', ' ')}` });
      loadContacts();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-black text-white py-4">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-2" />Back
            </Button>
            <h1 className="text-xl font-bold">Contact Requests</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">{total} total</span>
            <Button variant="outline" size="sm" onClick={loadContacts} className="border-gray-700 text-gray-300">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card className="p-4 mb-6 border-gray-200">
          <div className="flex items-center gap-4">
            <Filter className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filter:</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
          </div>
        ) : contacts.length === 0 ? (
          <Card className="p-12 text-center border-gray-200">
            <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No contact requests found</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {contacts.map((contact) => (
              <Card key={contact.ticketId} className="p-6 border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-mono text-gray-500">{contact.ticketId}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[contact.status]}`}>
                        {contact.status.replace('_', ' ')}
                      </span>
                      {contact.serviceType && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {contact.serviceType}
                        </span>
                      )}
                    </div>
                    
                    <h3 className="text-lg font-bold text-black">{contact.name}</h3>
                    
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1"><Mail className="h-4 w-4" />{contact.email}</span>
                      <span className="flex items-center gap-1"><Phone className="h-4 w-4" />{contact.phone}</span>
                    </div>

                    {contact.message && (
                      <p className="mt-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{contact.message}</p>
                    )}

                    <p className="mt-2 text-xs text-gray-400">Received: {formatDate(contact.createdAt)}</p>
                  </div>

                  <Select value={contact.status} onValueChange={(value) => handleStatusChange(contact.ticketId, value)}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContacts;
